import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';
import { AxiosError } from 'axios';
import { ArrowLeft, Info, User, Loader2, Save } from 'lucide-react';
import { updateProfileSchema, type ApiError, type UpdateProfileInput } from '@tracker/shared';
import { profileService } from '@/services/profile.service';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

function getErrorMessage(e: unknown): string {
  const ax = e as AxiosError<ApiError>;
  return ax.response?.data?.error?.message ?? 'Something went wrong';
}

function SectionHeader({ icon, title, hint }: { icon: React.ReactNode; title: string; hint?: string }) {
  return (
    <CardHeader className="border-b border-border/40 px-4 py-2.5">
      <CardTitle className="flex items-center gap-2 text-sm font-semibold text-primary">
        <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-primary/10">{icon}</div>
        {title}
        {hint && <span className="ml-auto text-xs font-normal text-muted-foreground">{hint}</span>}
      </CardTitle>
    </CardHeader>
  );
}

function FieldError({ message }: { message?: string }) {
  if (!message) return null;
  return <p className="mt-1 text-xs text-destructive">{message}</p>;
}


export function AccountManagePage() {
  const navigate = useNavigate();
  const { user, refreshMe } = useAuth();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<UpdateProfileInput>({
    resolver: zodResolver(updateProfileSchema),
    defaultValues: {},
  });

  useEffect(() => {
    if (user) {
      reset({
        first_name:           user.first_name ?? undefined,
        last_name:            user.last_name ?? undefined,
        phone:                user.phone ?? undefined,
      });
    }
  }, [user, reset]);

  const save = useMutation({
    mutationFn: (input: UpdateProfileInput) => profileService.updateMe(input),
    onSuccess: async () => {
      toast.success('Profile updated');
      await refreshMe();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const onSubmit = (values: UpdateProfileInput) => {
    // Normalize empty strings to null so a cleared phone is saved as empty
    const cleaned = Object.fromEntries(
      Object.entries(values).map(([k, v]) => [k, v === '' ? null : v]),
    ) as UpdateProfileInput;
    save.mutate(cleaned);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 pb-4">
      {/* Header */}
      <div className="flex items-start gap-3">
        <button
          type="button"
          onClick={() => navigate('/account')}
          className="mt-1 text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Manage Account</h1>
          <p className="text-sm text-muted-foreground">Update your personal information.</p>
        </div>
      </div>

      {/* Account Info (read-only) */}
      <Card>
        <SectionHeader icon={<Info className="h-3.5 w-3.5" />} title="Account Info" hint="Managed by admin" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input value={user?.email ?? ''} disabled className="bg-muted/40" />
            </div>
            <div className="space-y-1.5">
              <Label>Employee Code</Label>
              <Input value={user?.employee_code ?? '—'} disabled className="bg-muted/40" />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Basic Information */}
      <Card>
        <SectionHeader icon={<User className="h-3.5 w-3.5" />} title="Basic Information" />
        <CardContent className="p-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="first_name">First Name</Label>
              <Input id="first_name" {...register('first_name')} />
              <FieldError message={errors.first_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="last_name">Last Name</Label>
              <Input id="last_name" {...register('last_name')} />
              <FieldError message={errors.last_name?.message} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="phone">Phone</Label>
              <Input id="phone" {...register('phone')} />
              <FieldError message={errors.phone?.message} />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Footer */}
      <div className="flex justify-center gap-3 pt-1">
        <Button type="button" variant="outline" className="min-w-[140px]" onClick={() => navigate('/account')}>
          Cancel
        </Button>
        <Button type="submit" className="min-w-[180px]" disabled={save.isPending}>
          {save.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {save.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>
    </form>
  );
}
